import React, { useState, useMemo } from "react";
import {
  Check, X, ChevronDown, ChevronUp, FileSearch, AlertTriangle, Info,
  Copy, CheckCircle2, MapPin,
} from "lucide-react";
import { toast } from "sonner";
import { CitationLinks } from "./CitationLinks";

// Storage key for dismissed OCR banners (per-analysis). Capped to the most
// recent 200 acknowledgements so long-lived users don't grow this unbounded.
const OCR_DISMISS_KEY = "myae:ocr_banner_dismissed";
const OCR_DISMISS_CAP = 200;

// Read/write the "banners I've acknowledged" set (a JSON array of analysis ids).
const dismissedSet = () => {
  try { return new Set(JSON.parse(localStorage.getItem(OCR_DISMISS_KEY) || "[]")); }
  catch { return new Set(); }
};
const dismissBanner = (analysisId) => {
  const s = dismissedSet();
  s.add(analysisId);
  // Drop the oldest ids past the cap (Set preserves insertion order).
  let arr = Array.from(s);
  if (arr.length > OCR_DISMISS_CAP) arr = arr.slice(-OCR_DISMISS_CAP);
  try { localStorage.setItem(OCR_DISMISS_KEY, JSON.stringify(arr)); } catch { /* ignore */ }
};

// One row inside the top summary card
const Row = ({ ok, label, testid }) => (
  <div className="flex items-center gap-2 text-sm leading-relaxed" data-testid={testid}>
    {ok ? (
      <Check size={15} className="text-emerald-600 shrink-0" />
    ) : (
      <X size={15} className="text-zinc-300 shrink-0" />
    )}
    <span className={ok ? "text-zinc-800" : "text-zinc-400"}>{label}</span>
  </div>
);

const plural = (n, one, many) => (n === 1 ? `1 ${one}` : `${n} ${many || one + "s"}`);

// Small copy-to-clipboard button (icon-only in normal state).
const CopyButton = ({ value, label = "Copy", testid, variant = "icon" }) => {
  const [copied, setCopied] = useState(false);
  const handle = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(label === "Copy reference" ? "Reference copied to clipboard" : `${label} — copied to clipboard`);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      toast.error("Could not copy to clipboard — please copy manually.");
    }
  };
  if (variant === "button") {
    return (
      <button
        type="button"
        onClick={handle}
        data-testid={testid}
        className="no-print inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-200"
      >
        {copied ? <CheckCircle2 size={13} className="text-emerald-600" /> : <Copy size={13} />}
        {copied ? "Copied" : label}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={handle}
      aria-label={label}
      data-testid={testid}
      className="no-print shrink-0 text-zinc-400 hover:text-indigo-600 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-200 rounded"
    >
      {copied ? <CheckCircle2 size={13} className="text-emerald-600" /> : <Copy size={13} />}
    </button>
  );
};

/**
 * ExtractionSummary
 * Sections:
 *   1. Top "Document Extraction Summary" card with green ✓ ticks
 *   2. Dismissible low-confidence amber banner (only <90% AND not acknowledged)
 *   3. Collapsible "What MyAE Detected" panel with references, copy buttons,
 *      location chip and incomplete-references block
 * Print behaviour: the details panel is FORCED open in print (see .print-force-open
 * class) so exported PDFs show the full detected list.
 */
export default function ExtractionSummary({ summary, filename, analysisId }) {
  const [openDetails, setOpenDetails] = useState(false);
  const [showAllRefs, setShowAllRefs] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(() =>
    analysisId ? dismissedSet().has(analysisId) : false);

  // Which references are incomplete? Used to decorate the sample list.
  // MUST be called before any early return to keep hook order stable.
  const incompleteSet = useMemo(
    () => new Set(((summary && summary.incomplete_references) || []).map((r) => (r || "").trim().toLowerCase())),
    [summary],
  );

  if (!summary) return null;

  const {
    brief_detected,
    rubric_detected,
    guide_detected,
    submission_detected,
    pages, slides, sheets, images, tables,
    speaker_notes_detected,
    references_detected_count = 0,
    in_text_citations = 0,
    reference_style,
    reference_section_found,
    matched_in_text_citations,
    unmatched_citations,
    unused_references,
    unique_in_text_citations,
    citation_links = [],
    unused_reference_samples = [],
    references_sample = [],
    references_sample_shown = 0,
    incomplete_references = [],
    reference_location,
    ocr_used, ocr_pages, ocr_confidence, ocr_status,
    extraction_confidence, low_confidence,
  } = summary;

  const totalPages = pages || slides || sheets || 0;
  const style = reference_style && reference_style !== "Unknown" ? reference_style : null;
  const isIncomplete = (r) => incompleteSet.has((r || "").trim().toLowerCase());

  const allRefsText = (references_sample || []).map((r, i) => `${i + 1}. ${r}`).join("\n\n");

  const handleDismissBanner = () => {
    if (analysisId) dismissBanner(analysisId);
    setBannerDismissed(true);
  };

  return (
    <section className="mt-8" data-testid="report-extraction-summary">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
          <FileSearch size={16} className="text-emerald-600" />
        </div>
        <h2 className="font-display text-lg font-semibold text-zinc-900 tracking-tight">
          Document Extraction Summary
        </h2>
      </div>

      {/* Main card */}
      <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50/70 to-white p-5 print:break-inside-avoid">
        <p className="text-sm font-medium text-emerald-800 mb-4 flex items-center gap-2">
          <Check size={16} className="text-emerald-600" />
          Document successfully analysed
          {filename && <span className="text-zinc-500 font-normal">— {filename}</span>}
        </p>

        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
          <Row ok={brief_detected} label="Assignment brief detected" testid="ext-row-brief" />
          <Row ok={rubric_detected} label="Marking rubric detected" testid="ext-row-rubric" />
          <Row ok={guide_detected} label="Assignment guide detected" testid="ext-row-guide" />
          <Row ok={submission_detected} label="Submission detected" testid="ext-row-submission" />
          {totalPages > 0 && (
            <Row
              ok
              label={
                slides
                  ? `${plural(slides, "slide")} extracted`
                  : sheets
                    ? `${plural(sheets, "sheet")} extracted`
                    : `${plural(pages, "page")} extracted`
              }
              testid="ext-row-pages"
            />
          )}
          <Row
            ok={references_detected_count > 0 || !!reference_section_found}
            label={
              references_detected_count > 0
                ? `${plural(references_detected_count, "reference")} detected${style ? ` (${style})` : ""}`
                : reference_section_found
                  ? "Reference section detected — formatting could not be fully analysed"
                  : "No references detected"
            }
            testid="ext-row-refs"
          />
          <Row
            ok={in_text_citations > 0}
            label={
              in_text_citations > 0
                ? `${plural(in_text_citations, "in-text citation")} detected`
                : "No in-text citations detected"
            }
            testid="ext-row-citations"
          />
          {references_detected_count > 0 && matched_in_text_citations != null && (
            <Row
              ok={(unmatched_citations || 0) === 0}
              label={
                `${matched_in_text_citations} of ${unique_in_text_citations ?? matched_in_text_citations} citation${(unique_in_text_citations ?? 0) === 1 ? "" : "s"} matched to references`
                + ((unmatched_citations || 0) > 0 ? `, ${unmatched_citations} unmatched` : "")
                + ((unused_references || 0) > 0 ? `, ${unused_references} never cited` : "")
              }
              testid="ext-row-citation-links"
            />
          )}
          {images > 0 && (
            <Row
              ok={ocr_used}
              label={
                ocr_used
                  ? `${plural(images, "image")} processed with OCR: ${ocr_pages || images}`
                  : `${plural(images, "image")} detected`
              }
              testid="ext-row-images"
            />
          )}
          {tables > 0 && (
            <Row ok label={`${plural(tables, "table")} extracted`} testid="ext-row-tables" />
          )}
          {speaker_notes_detected && (
            <Row ok label="Speaker notes detected" testid="ext-row-speaker-notes" />
          )}
          {typeof extraction_confidence === "number" && (
            <Row
              ok
              label={`Extraction confidence: ${extraction_confidence}%`}
              testid="ext-row-confidence"
            />
          )}
        </div>

        {/* Low-confidence banner — only when < 90% AND not previously dismissed.
            High-confidence reports NEVER show this banner. */}
        {low_confidence && !bannerDismissed && (
          <div
            className="no-print mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-2.5"
            data-testid="ext-low-confidence-warning"
          >
            <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-xs text-amber-900 leading-relaxed">
                Some parts of this document were difficult to read
                {typeof extraction_confidence === "number"
                  ? ` (extraction confidence ${extraction_confidence}%)`
                  : ""}
                . Please review the extracted content and verify that references, tables and
                figures were captured correctly.
              </p>
            </div>
            <button
              type="button"
              onClick={handleDismissBanner}
              data-testid="ext-low-confidence-dismiss"
              className="shrink-0 text-amber-700 hover:text-amber-900 text-[11px] font-semibold underline underline-offset-2"
            >
              Got it
            </button>
          </div>
        )}
      </div>

      {/* "What MyAE Detected" — collapsible details (forced open in print) */}
      <div className="mt-3 rounded-2xl border border-zinc-200 bg-white overflow-hidden print:break-inside-avoid">
        <button
          type="button"
          onClick={() => setOpenDetails(!openDetails)}
          data-testid="ext-details-toggle"
          className="no-print w-full flex items-center justify-between px-5 py-3.5 text-sm font-medium text-zinc-800 hover:bg-zinc-50 transition-colors"
        >
          <span className="flex items-center gap-2">
            <Info size={15} className="text-indigo-500" />
            What MyAE Detected
          </span>
          {openDetails ? <ChevronUp size={16} className="text-zinc-500" /> : <ChevronDown size={16} className="text-zinc-500" />}
        </button>

        {/* Print-only header (so the PDF has a visible title above the auto-expanded content) */}
        <div className="hidden print:block px-5 pt-4">
          <p className="font-display text-sm font-semibold text-zinc-900 flex items-center gap-2">
            <Info size={13} className="text-indigo-500" /> What MyAE Detected
          </p>
        </div>

        {/* Details — auto-shown in print regardless of `openDetails` */}
        <div
          className={`${openDetails ? "block" : "hidden"} print:block border-t border-zinc-100 px-5 py-4 space-y-4`}
          data-testid="ext-details-panel"
        >
          {/* Summary chips */}
          <div className="flex flex-wrap gap-2">
            <span className="text-[11px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-100 px-2.5 py-1 rounded-full">
              {references_detected_count} references
            </span>
            <span className="text-[11px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-100 px-2.5 py-1 rounded-full">
              {in_text_citations} in-text citations
            </span>
            <span className="text-[11px] font-medium text-zinc-700 bg-zinc-50 border border-zinc-200 px-2.5 py-1 rounded-full">
              Referencing style: {style || "Not identified"}
            </span>
            {reference_location && (reference_location.slide || typeof reference_location.position_pct === "number") && (
              <span
                className="text-[11px] font-medium text-emerald-800 bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-full inline-flex items-center gap-1"
                data-testid="ext-reference-location"
                title="Location of the reference list within the extracted text"
              >
                <MapPin size={11} />
                {reference_location.slide
                  ? `Found on slide ${reference_location.slide}`
                  : `Found ~${reference_location.position_pct}% into the document`}
              </span>
            )}
            {ocr_used && (
              <span className="text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-full">
                OCR: {ocr_status === "successful" ? "successful" : ocr_status}
                {ocr_pages ? ` · ${ocr_pages} page${ocr_pages === 1 ? "" : "s"}` : ""}
                {typeof ocr_confidence === "number" ? ` · ${ocr_confidence}%` : ""}
              </span>
            )}
          </div>

          {/* Reference sample list */}
          {references_sample.length > 0 && (
            <div>
              <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                  Detected references
                  <span className="ml-2 font-normal text-zinc-400 normal-case tracking-normal">
                    {references_sample_shown} of {references_detected_count} shown
                  </span>
                </p>
                <CopyButton
                  value={allRefsText}
                  label={`Copy all ${references_sample.length} references`}
                  variant="button"
                  testid="ext-copy-all-refs-btn"
                />
              </div>
              <ol
                className="space-y-1.5 max-h-72 overflow-auto pr-1 print:max-h-none print:overflow-visible"
                data-testid="ext-references-list"
              >
                {(showAllRefs ? references_sample : references_sample.slice(0, 10)).map(
                  (r, i) => {
                    const incomplete = isIncomplete(r);
                    return (
                      <li
                        key={i}
                        className={
                          "group flex gap-2 items-start rounded-md px-2 py-1 " +
                          (incomplete
                            ? "bg-amber-50 border border-amber-200"
                            : "hover:bg-zinc-50")
                        }
                        data-testid={`ext-ref-item-${i}`}
                        data-incomplete={incomplete ? "true" : "false"}
                      >
                        <span className="font-mono text-xs text-zinc-400 shrink-0 w-6 mt-0.5">
                          {i + 1}.
                        </span>
                        <span className="flex-1 text-[13px] leading-relaxed text-zinc-700">
                          {r}
                          {incomplete && (
                            <span
                              className="ml-2 text-[10px] font-semibold text-amber-800 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded-full uppercase tracking-wide"
                              title="This entry may be missing a year, URL or details."
                            >
                              incomplete
                            </span>
                          )}
                        </span>
                        <span className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                          <CopyButton
                            value={r}
                            label="Copy reference"
                            testid={`ext-ref-copy-${i}`}
                          />
                        </span>
                      </li>
                    );
                  },
                )}
              </ol>
              {references_sample.length > 10 && (
                <button
                  type="button"
                  onClick={() => setShowAllRefs(!showAllRefs)}
                  data-testid="ext-references-toggle-all"
                  className="no-print mt-2 text-xs font-medium text-indigo-600 hover:text-indigo-700"
                >
                  {showAllRefs ? "Show fewer" : `Show all references (${references_sample.length})`}
                </button>
              )}
              {references_detected_count > references_sample.length && !showAllRefs && (
                <p className="text-[11px] text-zinc-400 mt-2">
                  {references_detected_count - references_sample.length} additional reference{references_detected_count - references_sample.length === 1 ? "" : "s"} detected but not shown individually.
                </p>
              )}
            </div>
          )}

          {/* Citation-linking table (editing assistant) */}
          <CitationLinks links={citation_links} unusedSamples={unused_reference_samples} />

          {/* Incomplete refs — separate call-out */}
          {incomplete_references.length > 0 && (
            <div
              className="rounded-xl border border-amber-200 bg-amber-50/60 p-3.5"
              data-testid="ext-incomplete-references"
            >
              <p className="text-xs font-semibold text-amber-900 mb-1.5 flex items-center gap-1.5">
                <AlertTriangle size={13} className="text-amber-600" />
                References that may be incomplete
              </p>
              <ul className="space-y-1 text-[12px] leading-relaxed text-amber-900/90">
                {incomplete_references.map((r, i) => (
                  <li key={i}>&ldquo;{r}&rdquo;</li>
                ))}
              </ul>
              <p className="text-[11px] text-amber-800/70 mt-1.5">
                These entries are missing a year, a URL, or appear too short to parse — please double-check them manually.
              </p>
            </div>
          )}

          {/* OCR block */}
          {ocr_used && (
            <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-3.5">
              <p className="text-xs font-semibold text-zinc-700 mb-1.5">OCR extraction</p>
              <p className="text-[12px] leading-relaxed text-zinc-600">
                Extracted <b>{ocr_pages || images} image{(ocr_pages || images) === 1 ? "" : "s"}</b> via Google Cloud Vision
                {typeof ocr_confidence === "number" ? ` with ${ocr_confidence}% confidence` : ""}. Text extracted from these images was
                merged into the main submission text and analysed alongside the rest of your document.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
